export interface Box {
	left: number;
	top: number;
	width: number;
	height: number;
}

const FLIGHT_DURATION = 1000;
const FLIGHT_STEPS = 10;
// how far the arc bends away from the straight line, as a fraction of the distance travelled
const ARC_LIFT = 0.25;
// extra size at the top of the arc (1.3 = 30% bigger)
const PEAK_SCALE = 1.3;
// minimum distance between the flying element and the viewport edge
const VIEWPORT_MARGIN = 8;
// the element first squeezes in place, as feedback that it was pressed, before it lifts off
const PRESS_OFFSET = 0.1;
const PRESS_SCALE = 0.9;

const round = (n: number) => Math.round(n * 100) / 100 + 0;

/**
 * Keyframes that squeeze an element positioned on `from` in place (press feedback), then move it
 * along an arc onto `to`, growing a little halfway and shrinking back down on landing.
 * The arc bends upward, or downward when there is no room above it within the viewport.
 * Translations are between the box centres; the scale follows the height ratio.
 */
export function flightKeyframes(from: Box, to: Box, viewportHeight: number): Keyframe[] {
	const fromCentreY = from.top + from.height / 2;
	const toCentreY = to.top + to.height / 2;
	const dx = (to.left + to.width / 2) - (from.left + from.width / 2);
	const dy = toCentreY - fromCentreY;
	const endScale = to.height / from.height;

	// the arc never bends further than `lift` beyond the higher (or lower) end point,
	// and the element is never taller than this along the way
	const halfPeakHeight = Math.max(from.height, to.height) * PEAK_SCALE / 2;
	const roomAbove = Math.min(fromCentreY, toCentreY) - halfPeakHeight - VIEWPORT_MARGIN;
	const roomBelow = viewportHeight - Math.max(fromCentreY, toCentreY) - halfPeakHeight - VIEWPORT_MARGIN;
	let lift = Math.hypot(dx, dy) * ARC_LIFT;
	if (lift > roomAbove) {
		lift = -Math.max(0, Math.min(lift, roomBelow));
	}

	const frames: Keyframe[] = [
		{ offset: 0, transform: 'translate(0px, 0px) scale(1)' },
	];
	for (let i = 0; i <= FLIGHT_STEPS; i++) {
		const t = i / FLIGHT_STEPS;
		// quadratic bezier with its control point moved, so the curve passes `lift` above (or, when negative, below) the midpoint
		const x = dx * t;
		const y = dy * t - 4 * lift * t * (1 - t);
		// start from the pressed size, and grow towards the peak and the target size from there
		const baseScale = PRESS_SCALE + (endScale - PRESS_SCALE) * t;
		const scale = baseScale * (1 + (PEAK_SCALE - 1) * Math.sin(Math.PI * t));
		frames.push({
			offset: round(PRESS_OFFSET + (1 - PRESS_OFFSET) * t),
			transform: `translate(${round(x)}px, ${round(y)}px) scale(${round(scale)})`,
		});
	}
	return frames;
}

/**
 * A copy of an element, fixed to the viewport on top of the original, that can then fly elsewhere.
 * Because the copy lives on <body>, the original may be removed from the page while it waits or flies.
 */
export class ElementFlight {
	private readonly from: DOMRect;
	private readonly copy: HTMLElement;
	private animation?: Animation;

	/** Places the copy exactly over `sourceEl`. */
	constructor(sourceEl: Element) {
		this.from = sourceEl.getBoundingClientRect();
		this.copy = sourceEl.cloneNode(true) as HTMLElement;
		this.copy.removeAttribute('id');
		Object.assign(this.copy.style, {
			position: 'fixed',
			left: `${this.from.left}px`,
			top: `${this.from.top}px`,
			width: `${this.from.width}px`,
			height: `${this.from.height}px`,
			margin: '0',
			zIndex: '60',
			pointerEvents: 'none',
		});
		document.body.appendChild(this.copy);
	}

	/**
	 * Flies the copy onto `targetEl`, or shrinks it away on the spot when there is no target.
	 * Resolves once the copy has been removed: true when it landed, false when the flight was cancelled.
	 */
	land(targetEl: Element | null): Promise<boolean> {
		const keyframes = targetEl ?
			flightKeyframes(this.from, targetEl.getBoundingClientRect(), window.innerHeight) :
			[{ transform: 'scale(1)', opacity: 1 }, { transform: 'scale(0)', opacity: 0 }];

		this.animation = this.copy.animate(keyframes, { duration: FLIGHT_DURATION, easing: 'ease-in-out', fill: 'forwards' });
		return this.animation.finished
			.then(() => true, () => false)
			.then((landed) => {
				this.copy.remove();
				return landed;
			});
	}

	/** Stops the flight, if any, and removes the copy. */
	cancel(): void {
		this.animation?.cancel();
		this.copy.remove();
	}
}

export function prefersReducedMotion(): boolean {
	return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
}
